import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { askForPaymentDate, reviewPaymentDateRequest, setPaymentDateForRecord, DATED_PAYMENT_STATUSES } from '../lib/api';
import type { NextPayment, PaymentRecord } from '../lib/api';

const rupees  = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDay  = (d: string) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtWhen = (iso: string) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
function addDays(day: string, n: number) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ── Next payment date, from the Payments page ────────────────────────────────
// Everything the Payment Dates page does, for one student, plus asking: email
// the student to choose a date (any time — not only after their 5th class) or
// remind them, set the date yourself, approve or reject the date they chose,
// or move an agreed date. They're emailed each time (when there's an email).

const PaymentDateModal: React.FC<{
  record: PaymentRecord;
  adminPassword: string;
  today: string;
  onClose: () => void;
  onChanged: (rowIndex: string, next: NextPayment | null) => void;   // null: reload
}> = ({ record, adminPassword, today, onClose, onChanged }) => {
  const np = record.nextPayment || null;
  const dated = !!np && DATED_PAYMENT_STATUSES.includes(np.status);
  const pending = np?.status === 'Pending';
  const first = record.name.split(' ')[0] || record.name;
  const email = record.studentEmail || record.email;
  const attended = record.classesAttended ?? record.classCount ?? 0;

  const [date, setDate] = useState(dated && np!.dueDate >= today ? np!.dueDate : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'' | 'ask' | 'date' | 'approve'>('');

  // Why no date can be asked for or set, if so (the server checks the same)
  const blocked =
    record.status === 'Ran Away' ? `${first} is marked Ran Away.`
    : !(record.courseFee > 0) ? `Set ${first}'s course fee first (Update Payment).`
    : record.balance <= 0 ? `${first} has nothing left to pay.`
    : record.studentId === '' ? `${record.name} isn't on the student roster yet — assign them to a mentor first, then you can ask for or set a payment date.`
    : '';

  const done = (message: string, res: { emailSentTo: string; nextPayment?: NextPayment }) => {
    toast.success(message);
    if (!res.emailSentTo) toast.info(`${record.name} has no email saved, so they weren't emailed — tell them by phone`);
    onChanged(record.rowIndex, res.nextPayment ?? null);
    onClose();
  };

  const run = async (kind: 'ask' | 'date' | 'approve', action: () => Promise<void>) => {
    setBusy(kind);
    try { await action(); } catch (err: any) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const ask = () => run('ask', async () => {
    const res = await askForPaymentDate(adminPassword, record.rowIndex);
    done(res.reminder ? `Reminder sent to ${res.emailSentTo}` : `Emailed ${res.emailSentTo} to choose a date`, res);
  });

  const approve = () => run('approve', async () => {
    const res = await reviewPaymentDateRequest(adminPassword, np!.requestId, 'Approved', '', note);
    done(`Approved — ${first} will pay on ${fmtDay(np!.requestedDate)}`, res);
  });

  const saveDate = () => {
    if (!date) { toast.error('Pick a date first'); return; }
    run('date', async () => {
      if (pending) {
        const res = await reviewPaymentDateRequest(adminPassword, np!.requestId, 'Rejected', date, note);
        done(`Not approved — ${first} has time till ${fmtDay(date)}`, res);
      } else {
        const res = await setPaymentDateForRecord(adminPassword, record.rowIndex, date, note);
        done(`${first}'s next payment date is ${fmtDay(date)}`, res);
      }
    });
  };

  // Where it stands now
  const state: { text: React.ReactNode; color: string } =
    !np ? {
      color: '#9ca3af',
      text: <>No payment date yet.{attended < 5 && <> {first} would be asked automatically after their 5th class ({attended} so far) — you can ask now.</>}</>,
    }
    : np.status === 'Asked' ? { color: '#9ca3af', text: <>Asked to choose a date{np.askedAt ? ` on ${fmtWhen(np.askedAt)}` : ''} — {first} hasn't chosen one yet.</> }
    : pending ? {
      color: '#F0B429',
      text: <>{first} wants to pay on <b>{fmtDay(np.requestedDate)}</b>.{np.studentNote && <span className="block mt-1 text-gray-500">“{np.studentNote}”</span>}</>,
    }
    : {
      color: np.dueDate < today ? '#dc2626' : np.dueDate === today ? '#16a34a' : '#2563eb',
      text: <>Pay by <b>{fmtDay(np.dueDate)}</b>{np.dueDate < today ? ' — overdue' : np.dueDate === today ? ' — due today' : ''}.
        <span className="block mt-1 text-gray-500">{np.status === 'Approved' ? 'The date they chose, approved.' : np.status === 'Rejected' ? 'Given by admin after their date was not approved.' : 'Set by admin.'}</span></>,
    };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
          <div>
            <h3 className="font-bold text-white">📅 Next payment date</h3>
            <p className="text-xs text-white/60 mt-0.5">{record.name}{record.phone ? ` · ${record.phone}` : ''}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg leading-none">×</button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-4">
          <div className="rounded-xl border border-gray-100 p-3.5 grid grid-cols-3 gap-3 text-center" style={{ background: '#f8f6ff' }}>
            {[
              { label: 'Still to pay', value: rupees(record.balance), color: '#dc2626' },
              { label: 'Paid', value: `${rupees(record.totalPaid)} / ${rupees(record.courseFee)}`, color: '#2b2240' },
              { label: 'Classes attended', value: String(attended), color: '#2b2240' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{s.label}</div>
                <div className="text-sm font-bold" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {blocked ? (
            <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-4 py-3">{blocked}</p>
          ) : (
            <>
              <div className="rounded-xl px-4 py-3 text-sm text-gray-700 bg-gray-50" style={{ borderLeft: `4px solid ${state.color}` }}>
                {state.text}
              </div>

              {pending && (
                <button onClick={approve} disabled={!!busy || np!.requestedDate < today}
                  title={np!.requestedDate < today ? 'That date has passed — give another date below' : undefined}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50" style={{ background: '#16a34a' }}>
                  {busy === 'approve' ? 'Approving…' : `✓ Approve ${fmtDay(np!.requestedDate)}`}
                </button>
              )}

              {(!np || np.status === 'Asked') && (
                <div>
                  <button onClick={ask} disabled={!!busy || !email}
                    className="w-full py-2.5 rounded-xl text-sm font-bold border-2 disabled:opacity-50"
                    style={{ borderColor: '#2b2240', color: '#2b2240' }}>
                    {busy === 'ask' ? 'Sending…' : np ? '📧 Send a reminder email' : `📧 Email ${first} to choose a date`}
                  </button>
                  <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
                    {email
                      ? <>Goes to {email} with a link to the Student Portal, where {first} chooses the date. You then approve it here or on Payment Dates.</>
                      : <>No email saved for {first} — set the date yourself below and tell them by phone.</>}
                  </p>
                </div>
              )}

              <div className="rounded-xl border-2 border-dashed border-gray-200 p-4 space-y-2.5">
                <p className="text-xs font-bold text-gray-700">
                  {pending ? `Or give ${first} another date` : dated ? 'Change the date' : 'Or set the date yourself'}
                </p>
                <input type="date" value={date} min={today} max={addDays(today, 365)} onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                <input value={note} onChange={e => setNote(e.target.value)} maxLength={500}
                  placeholder={email ? `Note to ${first} (optional — goes in the email)` : 'Note (optional)'}
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                <button onClick={saveDate} disabled={!!busy || !date}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                  style={{ background: pending ? '#dc2626' : '#2b2240' }}>
                  {busy === 'date' ? 'Saving…' : pending ? '✕ Not approved — give this date' : `Save date${email ? ' & email ' + first : ''}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentDateModal;
